import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "pfi_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try { return window.localStorage.getItem(TOKEN_KEY); } catch (e) { return null; }
  });
  const [loading, setLoading] = useState(true);

  const setSession = useCallback((newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    try {
      if (newToken) window.localStorage.setItem(TOKEN_KEY, newToken);
      else window.localStorage.removeItem(TOKEN_KEY);
    } catch (e) {}
  }, []);

  const fetchMe = useCallback(async (t) => {
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(res.data);
    } catch (e) {
      setSession(null, null);
    } finally {
      setLoading(false);
    }
  }, [setSession]);

  useEffect(() => {
    // If OAuth callback hash present, let AuthCallback handle it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setSession(res.data.token, res.data.user);
    return res.data;
  };

  const register = async (name, email, password, language) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, language });
    setSession(res.data.token, res.data.user);
    return res.data;
  };

  const logout = () => {
    setSession(null, null);
  };

  const consumeEmergentSession = async (sessionId) => {
    const res = await axios.post(`${API}/auth/emergent/session`, { session_id: sessionId });
    setSession(res.data.session_token, res.data.user);
    return res.data;
  };

  const authAxios = axios.create({ baseURL: API });
  authAxios.interceptors.request.use((cfg) => {
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
  });

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, consumeEmergentSession, authAxios, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
